import { optionService } from "../option";
import {
    ColorDefinition,
    CommandWithFollowup,
    ILayout,
    IllustrationElement,
    IllustrationStepData,
    LayoutElementType,
    LayoutsState,
    Region,
    Step,
    VariantResource,
} from "../../types";
import { RegionElement, StepService } from "../../types";
import { WorkflowManager } from "../../WorkflowManager";
import { LayoutNotFoundError, MisconfigurationError, ResourceNotFoundError } from "../../util/exception";
import { AssetNotFoundError } from "../../util/exception";
import { fetchAsString } from "../../util/crossplatform";
import {
    CanvasCommand,
    CreateElementCommand,
    DeleteElementCommand,
    GroupCommand,
    IllustrationCacheCommand,
    IllustrationColorCommand,
} from "../../command";
import { findElement } from "../../LayoutsState";
import { generateSVGWithUnknownColors, modifySVGWithElementProperties } from "../../util/illustration";
import { svgObjectURL } from "../../Elements/factory";
import { LayoutData } from "../../CommandContext";
import { generate } from "../../util/guid";

export interface SVGCreateOpts {
    stepName?: string;
    src: string;
    objectURL: string;
    svg: {
        svg: string;
        colors: { [key: string]: ColorDefinition };
    };
}

class IllustrationStepService implements StepService<IllustrationStepData> {
    async getIllustrationBody(src: string): Promise<string> {
        return new Promise((resolve) => {
            fetchAsString(src as string, true)
                .then((svg) => {
                    resolve(svg);
                })
                .catch((e) => console.error(e));
        });
    }

    getCreateElementCommand(id: string, region: Region, regionIndex: number, layout: ILayout, options: SVGCreateOpts) {
        return new CreateElementCommand<IllustrationElement>(
            {
                stepRegion: region,
                stepRegionIndex: regionIndex,
                stepName: options.stepName,
                colors: options.svg.colors,
                id,
                src: options.src,
                svg: options.svg.svg,
                cachedObjectURL: options.objectURL,
                type: LayoutElementType.Illustration,
                y: region.top,
                x: region.left,
                rotation: region.rotation,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                immutable: region.immutable,
            },
            layout,
        );
    }

    getColors(stepData: Step<IllustrationStepData>, workflowManager: WorkflowManager) {
        const regionEls = workflowManager.getRegionElements(stepData.stepName) || [];
        if (regionEls.length === 0) return [];
        try {
            const layoutElement = findElement(
                regionEls[0].id,
                workflowManager.getAllLayoutData(),
            ) as IllustrationElement;
            return layoutElement.colors;
        } catch (e) {
            return [];
        }
    }

    async init(
        stepData: Step<IllustrationStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        const option = stepData.option;
        if (!option) {
            return null;
        }
        if (reducerState) {
            return this.reload(stepData, workflowManager, reducerState);
        } else {
            const defaultVariant = optionService.getDefaultVariant(option);
            if (defaultVariant) {
                return await this.selectVariantCommand(stepData, defaultVariant, [], () => {}, workflowManager);
            }
        }
        return null;
    }

    private async reload(
        stepData: Step<IllustrationStepData>,
        workflowManager: WorkflowManager,
        reducerState: LayoutsState,
    ) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const option = stepData.option;
        if (serializedStep?.selectedVariants) {
            const variantId = serializedStep.selectedVariants[0].id;
            if (option && variantId) {
                const variant = option.variants?.find((variant: VariantResource) => variant.id === variantId);
                if (variant) {
                    const regionElements = elementsForStep.map((el) => ({
                        id: el.id,
                        region: el.stepRegion,
                        regionIndex: el.stepRegionIndex,
                    }));
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        [variant],
                        regionElements,
                        async () => {
                            workflowManager.updateMetadata(stepData.stepName, {
                                colors: serializedStep.storage?.colors,
                            });
                            workflowManager.setMandatoryFulfilled(stepData.stepName, true);
                        },
                    );
                }
            } else throw new Error("Required illustration variant no longer available");
        }
        return null;
    }

    async availableColors(stepData: Step<IllustrationStepData>, _workflowManager: WorkflowManager) {
        const colorOption = stepData.data.colorOption;
        if (colorOption) {
            return colorOption
                ? colorOption.variants?.map((variant: VariantResource) => {
                      return {
                          fill: variant.color,
                          stroke: variant.color,
                          variant,
                          pmsValue: variant.name,
                      };
                  })
                : [];
        }
        return [];
    }

    async changeColorsCommand(
        svg: string,
        illustrationWidth: number,
        illustrationHeight: number,
        elements: string[],
        newFills: Map<string, string | ColorDefinition>,
    ): Promise<CanvasCommand> {
        const newColors: { [key: string]: ColorDefinition } = {};
        for (const [className, newFill] of newFills.entries()) {
            if (typeof newFill === "string") {
                newColors[className] = {
                    browserValue: newFill,
                    spotColor: undefined,
                    pmsValue: undefined,
                };
            } else {
                newColors[className] = {
                    browserValue: newFill.browserValue,
                    spotColor: newFill.spotColor,
                    pmsValue: newFill.pmsValue,
                };
            }
        }
        const svgData = modifySVGWithElementProperties(svg, illustrationWidth, illustrationHeight, newColors);
        const objectURL = await svgObjectURL(svgData);
        const commands: CanvasCommand[] = [];
        for (const id of elements) {
            for (const [className, newFill] of newFills.entries()) {
                commands.push(new IllustrationColorCommand(id, className, newFill));
            }
            commands.push(new IllustrationCacheCommand(id, svgData, objectURL));
        }
        return new GroupCommand(commands);
    }

    async changeColors(
        stepData: Step<IllustrationStepData>,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
        getAllLayouts: () => LayoutData[],
        newFills: Map<string, string | ColorDefinition>,
    ) {
        if (elements.length === 0) {
            return;
        }

        const layoutElement = findElement(
            elements[0].id,
            getAllLayouts().map((layout) => layout.layoutState),
        ) as IllustrationElement;

        // Compute change in color and apply to metadata
        const newColors: { [key: string]: ColorDefinition } = { ...(layoutElement as IllustrationElement).colors };
        const storageColors: any = {};
        Object.entries(newColors).forEach(([key, value]) => {
            const color: any = { browserValue: value.browserValue, pmsValue: value.pmsValue };
            const spotColor = value.spotColor;
            if (spotColor) {
                color["spotColor"] = { profileName: spotColor.profileName, namedColor: spotColor.namedColor };
            }
            storageColors[key] = color;
        });
        for (const [className, newFill] of newFills.entries()) {
            const fill = typeof newFill === "string" ? newFill : newFill.browserValue;
            const pmsValue = typeof newFill === "string" ? undefined : newFill.pmsValue;
            newColors[className] = { browserValue: fill, spotColor: newColors[className]?.spotColor, pmsValue };
            storageColors[className] = { browserValue: fill };
        }

        let colors = Array.from(Object.values(newColors)).map((c) => c.browserValue);

        const colorOption = stepData.data.colorOption;
        if (colorOption) {
            colorOption.variants?.forEach((v: VariantResource) => {
                colors = colors.map((c) => (c.toLowerCase() === v.color?.toLowerCase() ? v.name : c));
            });
        }
        workflowManager.updateMetadata(stepData.stepName, { colors });
        const colorsForCommand: Map<string, ColorDefinition> = new Map();
        Object.entries(newColors).forEach(([key, value]) => {
            colorsForCommand.set(key, value);
        });

        if (!layoutElement.svg) {
            throw new Error("Colors changed before SVG loaded. This should never happen!");
        }

        const command = await this.changeColorsCommand(
            layoutElement.svg,
            layoutElement.width,
            layoutElement.height,
            elements.map((el) => el.id),
            colorsForCommand,
        );
        workflowManager.updateStorage(stepData.stepName, {
            colors: storageColors,
        });
        workflowManager.getCommandDispatcher()(command);
    }

    async selectVariant(
        stepData: Step<IllustrationStepData>,
        variant: VariantResource,
        elements: RegionElement[],
        setIsUpdating: (isUpdating: boolean) => void,
        workflowManager: WorkflowManager,
    ) {
        const result = await this.selectVariantCommand(stepData, variant, elements, setIsUpdating, workflowManager);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    private async selectVariantCommand(
        stepData: Step<IllustrationStepData>,
        variant: VariantResource,
        elements: RegionElement[],
        setIsUpdating: (isUpdating: boolean) => void,
        workflowManager: WorkflowManager,
    ): Promise<CommandWithFollowup | null> {
        if (!stepData.data || !stepData.data.regions) {
            throw new MisconfigurationError(stepData, "Missing regions.");
        }
        setIsUpdating(true);
        const deletionCommands = elements.map((el) => new DeleteElementCommand(el.id));
        if (stepData.mandatory) {
            workflowManager.setMandatoryFulfilled(stepData.stepName, false);
        }
        const asset = variant.asset;
        if (!asset) {
            throw new AssetNotFoundError(variant);
        }
        const link = asset.fileLink;

        if (!link) {
            throw new ResourceNotFoundError(asset);
        }

        const svgData = await generateSVGWithUnknownColors(await this.getIllustrationBody(link));
        const objectURL = await svgObjectURL(svgData.svg);
        const createElementForRegion = (region: Region, regionIndex: number) => {
            const layout = workflowManager.getLayouts().find((layout: ILayout) => layout.panelId === region.panelId);
            if (!layout) {
                throw new LayoutNotFoundError(region);
            }
            const elementKey = generate();
            return {
                regionElement: { id: elementKey, region, regionIndex },
                command: this.getCreateElementCommand(elementKey, region, regionIndex, layout, {
                    stepName: stepData.stepName,
                    src: link,
                    objectURL,
                    svg: svgData,
                }),
            };
        };
        const elementsWithCommands = stepData.data.regions.map(createElementForRegion);
        const createCommands = elementsWithCommands.map((r) => r.command);
        const commandList: CanvasCommand[] = [...createCommands, ...deletionCommands];

        let colors = Array.from(Object.values(svgData.colors)).map((c) => c.browserValue);
        const colorOption = stepData.data.colorOption;
        if (colorOption) {
            colorOption.variants?.forEach((v: VariantResource) => {
                colors = colors.map((c) => (c.toLowerCase() === v.color?.toLowerCase() ? v.name : c));
            });
        }

        workflowManager.updateMetadata(stepData.stepName, { colors: colors });
        return {
            command: new GroupCommand(commandList),
            followup: async () => {
                await workflowManager.setSelectionsAndElements(
                    stepData.stepName,
                    [variant],
                    elementsWithCommands.map((r) => r.regionElement),
                    async () => {
                        workflowManager.setMandatoryFulfilled(stepData.stepName, true);
                        setIsUpdating(false);
                    },
                );

                // If the illustration has an asset configuration then we need to apply it.
                const defaultColorVariants = asset.assetConfiguration?.defaultColorVariants || [];
                if (defaultColorVariants.length !== 0) {
                    const modifiedColors: { [id: string]: ColorDefinition } = {};
                    const channelNumbers = asset.assetConfiguration?.channelNumbers || [];
                    channelNumbers.forEach((cn) => {
                        const v = defaultColorVariants.find((item) => item.channelNumber === cn.number);
                        if (v) {
                            modifiedColors[`${cn.id.replace(/\W/g, "")}`] = { browserValue: v?.variant?.color || "" };
                        }
                    });
                    await this.changeColors(
                        stepData,
                        elementsWithCommands.map((r) => r.regionElement),
                        workflowManager,
                        () => workflowManager.getCommandContext().getAllLayouts(),
                        new Map(Object.entries(modifiedColors)),
                    );
                }

                if (stepData.data.colorPickerEnabled) {
                    // When the color picker is enabled & only 1 variant is available, we immediately apply
                    // the variant to the illustration.
                    const availableColors = (await this.availableColors(stepData, workflowManager)) || [];
                    const colorEntries = Object.keys(svgData.colors);
                    const singleColor = availableColors.length === 1 && colorEntries.length === 1;
                    if (singleColor) {
                        const color = availableColors[0];
                        const entry = colorEntries[0];
                        await this.changeColors(
                            stepData,
                            elementsWithCommands.map((r) => r.regionElement),
                            workflowManager,
                            () => workflowManager.getCommandContext().getAllLayouts(),
                            new Map([[entry, color.variant.color!]]),
                        );
                    }

                    if (!singleColor && defaultColorVariants.length === 0) {
                        // This "change" is just to populate color metadata.
                        await this.changeColors(
                            stepData,
                            elementsWithCommands.map((r) => r.regionElement),
                            workflowManager,
                            () => workflowManager.getCommandContext().getAllLayouts(),
                            new Map(),
                        );
                    }
                }
            },
        };
    }
}

export const illustrationStepService = new IllustrationStepService();
